import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Modal } from '../../../shared/components/Modal';
import { Select } from '../../../shared/components/Select';
import { Toggle } from '../../../shared/components/Toggle';
import { useCreateSalesContact, useUpdateSalesContact } from '../hooks/useSalesContacts';
import {
  BUILD_STATUS_LABELS,
  SOURCE_LABELS,
  STAGE_LABELS,
  SalesContactBuildStatus,
  SalesContactSource,
  SalesContactStage,
  type CreateSalesContactPayload,
  type SalesContact,
  type UpdateSalesContactPayload,
} from '../types';

interface Props {
  /** Absent = create mode. */
  contact?: SalesContact;
  onClose: () => void;
  /** Called with the new contact's id after a successful create. */
  onCreated?: (id: string) => void;
}

const optionsFrom = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, title]) => ({ value, title }));

/** '' → undefined so an untouched optional field is omitted rather than sent as an empty string. */
const text = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/** '' → undefined, otherwise a number; NaN is dropped so a half-typed value never posts. */
const num = (value: string) => {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Create/edit a sales contact.
 *
 * ⚠️ `stage` and `buildStatus` are rendered ONLY in edit mode. They are absent from
 * order-service's CreateSalesContactDto — a new contact always starts PROSPECT/NONE — so
 * offering them at create time would promise something the API does not accept.
 */
export function ContactFormModal({ contact, onClose, onCreated }: Props) {
  const isEdit = !!contact;
  const createContact = useCreateSalesContact();
  const updateContact = useUpdateSalesContact();

  const [name, setName] = useState(contact?.name ?? '');
  const [businessName, setBusinessName] = useState(contact?.businessName ?? '');
  const [role, setRole] = useState(contact?.role ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [igHandle, setIgHandle] = useState(contact?.igHandle ?? '');
  const [otherHandles, setOtherHandles] = useState((contact?.otherHandles ?? []).join(', '));
  const [website, setWebsite] = useState(contact?.website ?? '');
  const [source, setSource] = useState<SalesContactSource>(
    contact?.source ?? SalesContactSource.MANUAL,
  );
  const [stage, setStage] = useState<SalesContactStage>(
    contact?.stage ?? SalesContactStage.PROSPECT,
  );
  const [buildStatus, setBuildStatus] = useState<SalesContactBuildStatus>(
    contact?.buildStatus ?? SalesContactBuildStatus.NONE,
  );
  const [solutionInterest, setSolutionInterest] = useState(contact?.solutionInterest ?? '');
  const [category, setCategory] = useState(contact?.category ?? '');
  const [primaryAudience, setPrimaryAudience] = useState(contact?.primaryAudience ?? '');
  const [visualQualityScore, setVisualQualityScore] = useState(
    contact?.visualQualityScore != null ? String(contact.visualQualityScore) : '',
  );
  const [growthMindsetScore, setGrowthMindsetScore] = useState(
    contact?.growthMindsetScore != null ? String(contact.growthMindsetScore) : '',
  );
  const [igFollowerCount, setIgFollowerCount] = useState(
    contact?.igFollowerCount != null ? String(contact.igFollowerCount) : '',
  );
  const [hasCelebrityOrPressAttention, setHasCelebrityOrPressAttention] = useState(
    contact?.hasCelebrityOrPressAttention ?? false,
  );
  const [assetsFolderUrl, setAssetsFolderUrl] = useState(contact?.assetsFolderUrl ?? '');
  const [designLinkUrl, setDesignLinkUrl] = useState(contact?.designLinkUrl ?? '');
  const [assignedRepId, setAssignedRepId] = useState(contact?.assignedRepId ?? '');

  const base: CreateSalesContactPayload = {
    name: name.trim(),
    businessName: text(businessName),
    role: text(role),
    email: text(email),
    phone: text(phone),
    igHandle: text(igHandle),
    otherHandles: otherHandles
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean),
    website: text(website),
    source,
    solutionInterest: text(solutionInterest),
    category: text(category),
    primaryAudience: text(primaryAudience),
    visualQualityScore: num(visualQualityScore),
    growthMindsetScore: num(growthMindsetScore),
    hasCelebrityOrPressAttention,
    igFollowerCount: num(igFollowerCount),
    assetsFolderUrl: text(assetsFolderUrl),
    designLinkUrl: text(designLinkUrl),
    assignedRepId: text(assignedRepId),
  };

  const submit = async () => {
    if (!base.name) return;
    if (isEdit && contact) {
      const payload: UpdateSalesContactPayload = { ...base, stage, buildStatus };
      await updateContact.mutateAsync({ id: contact.id, payload });
    } else {
      const created = await createContact.mutateAsync(base);
      onCreated?.(created.id);
    }
    onClose();
  };

  const isPending = createContact.isPending || updateContact.isPending;

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto themed-scrollbar">
        <h2 className="text-[18px] font-semibold text-foreground">
          {isEdit ? 'Edit contact' : 'New contact'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Name *" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Business name"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
          />
          <Input label="Role" value={role} onChange={(e) => setRole(e.target.value)} />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <Input
            label="Instagram handle"
            value={igHandle}
            onChange={(e) => setIgHandle(e.target.value)}
            placeholder="@brand"
          />
          <Input
            label="Other handles (comma separated)"
            value={otherHandles}
            onChange={(e) => setOtherHandles(e.target.value)}
            placeholder="tiktok:@brand, x:@brand"
          />
          {/* type="url" on the three @IsUrl fields — order-service rejects a bare domain
              with a 400, so the browser's own hint is worth having. */}
          <Input
            label="Website"
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://brand.example"
          />
          <Select
            label="Source"
            options={optionsFrom(SOURCE_LABELS)}
            value={source}
            onChange={(value) => setSource(value as SalesContactSource)}
          />
          <Input
            label="Solution interest"
            value={solutionInterest}
            onChange={(e) => setSolutionInterest(e.target.value)}
            placeholder="Rack & Box"
          />
          <Input label="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
          <Input
            label="Primary audience"
            value={primaryAudience}
            onChange={(e) => setPrimaryAudience(e.target.value)}
          />
          <Input
            label="Visual quality score"
            type="number"
            min={0}
            value={visualQualityScore}
            onChange={(e) => setVisualQualityScore(e.target.value)}
          />
          <Input
            label="Growth mindset score"
            type="number"
            min={0}
            value={growthMindsetScore}
            onChange={(e) => setGrowthMindsetScore(e.target.value)}
          />
          <Input
            label="Instagram followers"
            type="number"
            min={0}
            value={igFollowerCount}
            onChange={(e) => setIgFollowerCount(e.target.value)}
          />
          <Input
            label="Assigned rep id"
            value={assignedRepId}
            onChange={(e) => setAssignedRepId(e.target.value)}
          />
          <Input
            label="Assets folder URL"
            type="url"
            value={assetsFolderUrl}
            onChange={(e) => setAssetsFolderUrl(e.target.value)}
            placeholder="https://drive.example/assets"
          />
          <Input
            label="Design link URL"
            type="url"
            value={designLinkUrl}
            onChange={(e) => setDesignLinkUrl(e.target.value)}
            placeholder="https://figma.example/design"
          />

          {/* Update-only — absent from CreateSalesContactDto, so never offered at create time. */}
          {isEdit && (
            <>
              <Select
                label="Stage"
                options={optionsFrom(STAGE_LABELS)}
                value={stage}
                onChange={(value) => setStage(value as SalesContactStage)}
              />
              <Select
                label="Build status"
                options={optionsFrom(BUILD_STATUS_LABELS)}
                value={buildStatus}
                onChange={(value) => setBuildStatus(value as SalesContactBuildStatus)}
              />
            </>
          )}
        </div>

        <Toggle
          label="Has celebrity or press attention"
          checked={hasCelebrityOrPressAttention}
          onChange={setHasCelebrityOrPressAttention}
        />

        {!isEdit && (
          <p className="text-[12px] text-muted-foreground">
            New contacts start at <span className="text-foreground">Prospect</span> with build
            status <span className="text-foreground">Not started</span>. Both are set from the
            contact's own page once it exists.
          </p>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" size="sm" className="h-[40px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-[40px]"
            onClick={submit}
            disabled={!base.name || isPending}
            loading={isPending}
          >
            {isEdit ? 'Save contact' : 'Create contact'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
